import { CheckoutContext } from '@/contexts/CheckoutContext';
import useSettings from '@/hooks/useSettings';
import { PaymentMethodOption, ProductData } from '@/types';
import { applyTransparency } from '@/utils/colors';
import { formatPrice } from '@/utils/format';
import { getRecurrencePeriodLabel } from '@/utils/payment';
import { useContext } from 'react';

type ProductComponentProps = {
  isLoading: boolean;
  price: number;
  product: ProductData['product'];
  sellerEmail: string;
  type?: string;
  recurrencePeriod?: number;
  paymentMethods?: PaymentMethodOption[];
};

const ProductComponent = ({
  isLoading,
  price = 0,
  product,
  type,
  recurrencePeriod
}: ProductComponentProps) => {
  const image = product?.image;
  const calculatedInstallments = product?.calculatedInstallments || [];
  const name = product?.name;
  const productPrice = product?.price || 0;

  const { canBePaidInInstallments } = useContext(CheckoutContext);

  if (isLoading) {
    return (
      <div className="col-span-full mb-3 rounded-md overflow-hidden">
        <div className="px-1 pt-2 pb-3 flex">
          <svg
            className="w-24 h-24 object-cover rounded-md mr-4 dark:bg-gray-300  bg-gray-200 animate-pulse"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            viewBox="-1 -3 18 26"
          >
            <path d="M14.066 0H7v5a2 2 0 0 1-2 2H0v11a1.97 1.97 0 0 0 1.934 2h12.132A1.97 1.97 0 0 0 16 18V2a1.97 1.97 0 0 0-1.934-2ZM10.5 6a1.5 1.5 0 1 1 0 2.999A1.5 1.5 0 0 1 10.5 6Zm2.221 10.515a1 1 0 0 1-.858.485h-8a1 1 0 0 1-.9-1.43L5.6 10.039a.978.978 0 0 1 .936-.57 1 1 0 0 1 .9.632l1.181 2.981.541-1a.945.945 0 0 1 .883-.522 1 1 0 0 1 .879.529l1.832 3.438a1 1 0 0 1-.031.988Z" />
            <path d="M5 5V.13a2.96 2.96 0 0 0-1.293.749L.879 3.707A2.98 2.98 0 0 0 .13 5H5Z" />
          </svg>
          <div className="flex flex-col gap-2">
            <div className="h-[20px] bg-gray-200 rounded-full dark:bg-gray-300 w-32 animate-pulse"></div>

            <div className="h-[15px] bg-gray-200 rounded-full dark:bg-gray-300 w-28 animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  const { text } = useSettings();

  const { installment: maxInstallment, value } = calculatedInstallments.reduce(
    (maxItem: any, item: any) => {
      return item.installment > maxItem.installment ? item : maxItem;
    },
    { installment: 0, value: 0 }
  );

  const getPrice = () => {
    if (type === 'subscription') {
      return `${formatPrice(price)} / ${getRecurrencePeriodLabel(recurrencePeriod || 30)}`;
    }

    if (!canBePaidInInstallments) {
      return '';
    }

    return `${maxInstallment} X de ${formatPrice(value)}`;
  };

  return (
    <div className="col-span-full mb-3 text-white rounded-md overflow-hidden">
      <div className="px-1 pt-2 pb-3 flex">
        {image && (
          <img
            src={image}
            alt="Imagem do produto"
            className="w-24 h-24 object-cover rounded-md mr-4"
          />
        )}
        <div>
          <h2 style={{ color: text?.color?.primary }} className="text-xl font-bold color-primary">
            {name}
          </h2>
          {productPrice > price && (
            <div
              style={{ color: applyTransparency(text?.color?.secondary, 0.6) }}
              className="text-gray-400 line-through"
            >{`De ${formatPrice(productPrice)}`}</div>
          )}
          {
            <div
              style={{ color: text?.color?.active }}
              className="text-1xl font-bold text-green-400"
            >
              {getPrice()}
            </div>
          }
          {type !== 'subscription' && (
            <div
              style={{
                color: canBePaidInInstallments
                  ? applyTransparency(text?.color?.secondary, 0.4)
                  : text.color.active
              }}
              className="text-gray-300"
            >{`${canBePaidInInstallments ? 'ou' : ''} ${formatPrice(price)} à vista`}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductComponent;
